import { useEffect, useMemo, useState } from 'react';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { useTemplates } from '../../data/useTemplates';
import { TemplatesTable } from './components/TemplatesTable';
import { TemplatesGrid } from './components/TemplatesGrid';
import { TemplatesList } from './components/TemplatesList';
import { STATUS_LABEL_KEYS } from './components/TemplateStatusBadge';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { formatDateTime } from '../../lib/format';
import { usePersistedState } from '../../lib/usePersistedState';
import { useT } from '../../i18n/I18nProvider';

type KpiKey = 'all' | 'draft' | 'submitted' | 'approved' | 'rejected' | 'paused';

export default function TemplatesPage() {
  const t = useT();
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useTemplates();
  const all = useMemo(() => data?.data ?? [], [data]);

  const [view, setView] = usePersistedState<ViewMode>('admin.templates.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.templates.pageSize', 25);

  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const counts = useMemo(() => ({
    all: all.length,
    draft: all.filter((tpl) => tpl.status === 'draft').length,
    submitted: all.filter((tpl) => tpl.status === 'submitted').length,
    approved: all.filter((tpl) => tpl.status === 'approved').length,
    rejected: all.filter((tpl) => tpl.status === 'rejected').length,
    paused: all.filter((tpl) => tpl.status === 'paused').length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',       label: t('templates.kpi.all'),       value: counts.all,       tone: 'primary' },
    { id: 'submitted', label: t('templates.kpi.submitted'), value: counts.submitted, tone: 'pending' },
    { id: 'approved',  label: t('status.approved'),         value: counts.approved,  tone: 'healthy' },
    { id: 'rejected',  label: t('status.rejected'),         value: counts.rejected,  tone: 'down' },
    { id: 'draft',     label: t('status.draft'),            value: counts.draft,     tone: 'neutral' },
    // Paused was computed and filterable through the drawer but had no chip,
    // so one of the five lifecycle states was invisible in the row that exists
    // to show all of them.
    { id: 'paused',    label: t('status.paused'),           value: counts.paused,    tone: 'warning' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const categories = Array.from(new Set(all.map((tpl) => tpl.category))).sort();
    const languages = Array.from(new Set(all.map((tpl) => tpl.language))).sort();
    return [
      {
        key: 'status',
        label: t('common.status'),
        placeholder: t('templates.filters.statusPlaceholder'),
        options: (['draft', 'submitted', 'approved', 'rejected', 'paused'] as const).map((s) => ({ value: s, label: t(STATUS_LABEL_KEYS[s]) }))
      },
      {
        key: 'category',
        label: t('templates.columns.category'),
        placeholder: t('templates.filters.categoryPlaceholder'),
        options: categories.map((c) => ({ value: c, label: c }))
      },
      {
        key: 'language',
        label: t('common.language'),
        placeholder: t('templates.filters.languagePlaceholder'),
        options: languages.map((l) => ({ value: l, label: l }))
      }
    ];
  }, [all, t]);

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters = Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery(''); setKpi('all'); setFilters({}); setPage(1);
  }

  // What is narrowing the catalogue, each pill removable on its own.
  const pills: ActiveFilterPill[] = [];
  if (query.trim()) pills.push({ id: 'query', field: t('common.search'), value: query.trim() });
  if (kpi && kpi !== 'all') {
    pills.push({ id: 'kpi', field: t('common.status'), value: kpiItems.find((i) => i.id === kpi)?.label ?? kpi });
  }
  if (filters.status) {
    const code = filters.status as keyof typeof STATUS_LABEL_KEYS;
    pills.push({ id: 'status', field: t('common.status'), value: STATUS_LABEL_KEYS[code] ? t(STATUS_LABEL_KEYS[code]) : filters.status });
  }
  if (filters.category) pills.push({ id: 'category', field: t('templates.columns.category'), value: filters.category });
  if (filters.language) pills.push({ id: 'language', field: t('common.language'), value: filters.language });

  function removePill(id: string) {
    setPage(1);
    if (id === 'query') return setQuery('');
    if (id === 'kpi') return setKpi('all');
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  // The explainer only appears when the distinction is actually on the page.
  const anyDevFallback = all.some((tpl) => tpl.dev_fallback);

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi && kpi !== 'all') rows = rows.filter((tpl) => tpl.status === kpi);
    if (filters.status) rows = rows.filter((tpl) => tpl.status === filters.status);
    if (filters.category) rows = rows.filter((tpl) => tpl.category === filters.category);
    if (filters.language) rows = rows.filter((tpl) => tpl.language === filters.language);
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (tpl) => tpl.code.toLowerCase().includes(q) || tpl.category.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [all, kpi, filters, query]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.section.messaging')}
        title={t('templates.title')}
        description={t('templates.description')}
        actions={
          <Stack gap="xs" align="flex-end">
            <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
              {t('action.refresh')}
            </Button>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('common.lastUpdated', { time: formatDateTime(new Date(dataUpdatedAt).toISOString()) })}
            </Text>
          </Stack>
        }
      />

      {/* The lifecycle split is the page's headline, so it sits above the
          table on the page ground rather than inside its chrome. */}
      <KpiFilterRow items={kpiItems} value={kpi} onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }} />
      <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />

      <TableShell
        title={t('templates.table.title')}
        totalLabel={t('templates.table.total', { shown: filtered.length, total: all.length })}
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('templates.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer fields={filterFields} values={filters} onApply={(next) => { setFilters(next); setPage(1); }} />
          </Group>
        }
        view={view}
        onViewChange={setView}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <EmptyState
            title={t('templates.empty')}
            description={hasActiveFilters ? t('templates.emptyFiltered') : t('templates.emptyHint')}
            action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
          />
        }
        error={error
          ? (
            <Alert color="error" title={t('templates.loadError')}>
              <Group gap="sm">
                <Text role="bodySmall">{error instanceof Error ? error.message : t('common.unknownError')}</Text>
                <Button size="xs" variant="outline" onClick={() => void refetch()}>{t('action.retry')}</Button>
              </Group>
            </Alert>
          )
          : null}
        pagination={
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        }
      >
        {view === 'table' && <TemplatesTable rows={paged} loading={isLoading} />}
        {view === 'cards' && <TemplatesGrid records={paged} />}
        {view === 'list'  && <TemplatesList records={paged} />}
      </TableShell>

      {anyDevFallback && (
        <Alert color="info" variant="light" title={t('templates.devFallbackNotice.title')}>
          {t('templates.devFallbackNotice.body')}
        </Alert>
      )}
    </Stack>
  );
}
